import { FC, useState } from "react";
import FocusTrap from "focus-trap-react";
import { motion } from "framer-motion";
import { map } from "lodash";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import { Overlay } from "shared/components";
import { getIcon, iconMap } from "shared/utils";
import { slideInFromTop, zIndex } from "shared/styles/constants";
import { StyledActionDropdownCloseButton } from "shared/components/ActionDropdown/styles";
import { ITheme } from "shared/types";
import {
  IconDropdownContainer,
  StyledIconItem,
  IconSelectDropdownTrigger,
  IconSelectWrapper,
} from "./styles";

interface IconSelectProps {
  value?: string;
  setValue: (value: string) => void;
}

export const IconSelect: FC<IconSelectProps> = ({ value, setValue }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedIcon = getIcon(value || "default");

  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] =
    useState<HTMLDivElement | null>(null);
  const { styles } = usePopper(referenceElement, popperElement, {
    placement: "bottom-start",
    strategy: "fixed",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  const handleClick = (key: string) => {
    setValue(key);
    setIsOpen(false);
  };

  return (
    <IconSelectWrapper ref={setReferenceElement}>
      <IconSelectDropdownTrigger
        isOpen={isOpen}
        handleClick={() => setIsOpen(!isOpen)}
        icon={selectedIcon}
        iconSize="xl"
      />
      {isOpen && (
        <>
          {createPortal(
            <>
              {/* close on outside dropdown click */}
              <Overlay onClick={() => setIsOpen(false)} />
              <div
                ref={setPopperElement}
                style={{ zIndex: zIndex.dropdown, ...styles.popper }}
              >
                <FocusTrap
                  focusTrapOptions={{
                    allowOutsideClick: true,
                  }}
                >
                  <motion.div {...slideInFromTop}>
                    <IconDropdownContainer>
                      {map(iconMap, ({ key, icon }) => (
                        <StyledIconItem
                          key={key}
                          icon={icon}
                          onClick={() => handleClick(key)}
                          iconSize="l"
                          theme={ITheme.secondary}
                        />
                      ))}
                      <StyledActionDropdownCloseButton
                        onClick={() => setIsOpen(false)}
                      >
                        Close
                      </StyledActionDropdownCloseButton>
                    </IconDropdownContainer>
                  </motion.div>
                </FocusTrap>
              </div>
            </>,
            document.body
          )}
        </>
      )}
    </IconSelectWrapper>
  );
};
